import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { BusyIcon } from '../../components/loading';   // shared loader symbol (§18.6)
import { QueryDatePicker } from '../modules/QueryDatePicker';   // shared date picker (Query Panel standard)
import { useModalKeys } from '../../hooks/useModalKeys';
import { useEddResultStore, type EddResult } from './eddResultStore';

interface TemplateOption { id: string; name: string; }

interface EddReportQueryModalProps {
  open: boolean;
  onClose: () => void;
}

// Each cascading dropdown → the SP @FillName it maps to.
type MultiKey = 'JobID' | 'ClientName' | 'ProjectID' | 'ProjectName' | 'SampleCategory' | 'Test' | 'Method';
type DateKey = 'dateReceivedFrom' | 'dateReceivedTo' | 'dateCollectedFrom' | 'dateCollectedTo';

const EMPTY_MULTI: Record<MultiKey, string[]> = {
  JobID: [], ClientName: [], ProjectID: [], ProjectName: [], SampleCategory: [], Test: [], Method: [],
};

// fillName → its key in the filter body.  A field's own selection is EXCLUDED
// when loading that field's own list, so ticking a checkbox never reloads it
// (cascade reflects the OTHER fields only — user request 2026-07-09).
const FILTER_KEY: Record<MultiKey, string> = {
  JobID: 'jobId', ClientName: 'client', ProjectID: 'projectId', ProjectName: 'projectName',
  Test: 'test', Method: 'method', SampleCategory: 'sampleCategory',
};

/**
 * "EDD Report Query" popup, ported to Alpaca Intelligence and dressed in the
 * STANDARD Query Panel chrome (`.qs-*` — same as ModuleListView's Query Panel):
 * orange titlebar, two-column `.qs-grid` of `.qs-field` rows, and a footer with
 * CLEAR · CANCEL · solid-orange OK.  The user picks an EDD Template (required) +
 * optional cascading filters, then OK previews every sheet of the template and
 * opens the tabbed Spreadsheet View.
 *
 * NOTE (Task 1 deviation): the shared `QueryCombo` is hard-wired to the generic
 * `/api/v1/modules/{key}/distinct/{column}` endpoint and can't be pointed at the
 * template-driven `/api/v1/edd/filter-options`, so the cascading combos here are
 * a `.qs-*`-styled multi-select (`EddQsCombo`) that reuses that EDD endpoint and
 * the SAME chrome/tokens.  Date fields DO use the shared `QueryDatePicker`.
 */
export default function EddReportQueryModal({ open, onClose }: EddReportQueryModalProps) {
  const notify = useNotify();
  const navigate = useNavigate();
  const setResult = useEddResultStore((s) => s.setResult);

  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [multi, setMulti] = useState<Record<MultiKey, string[]>>(EMPTY_MULTI);
  const [dates, setDates] = useState({
    dateReceivedFrom: '', dateReceivedTo: '', dateCollectedFrom: '', dateCollectedTo: '',
  });
  const [generating, setGenerating] = useState(false);

  // Load templates once when opened.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get('/api/v1/edd/templates')
      .then((res) => { if (alive) setTemplates((res.data?.data ?? []) as TemplateOption[]); })
      .catch(() => notify.error('Failed to load EDD templates.'));
    return () => { alive = false; };
  }, [open, notify]);

  const filterBody = useMemo(() => ({
    jobId:          multi.JobID,
    client:         multi.ClientName,
    projectId:      multi.ProjectID,
    projectName:    multi.ProjectName,
    test:           multi.Test,
    method:         multi.Method,
    sampleCategory: multi.SampleCategory,
    dateReceivedFrom:  dates.dateReceivedFrom  || null,
    dateReceivedTo:    dates.dateReceivedTo    || null,
    dateCollectedFrom: dates.dateCollectedFrom || null,
    dateCollectedTo:   dates.dateCollectedTo   || null,
  }), [multi, dates]);

  const reset = useCallback(() => {
    setTemplateId('');
    setMulti(EMPTY_MULTI);
    setDates({ dateReceivedFrom: '', dateReceivedTo: '', dateCollectedFrom: '', dateCollectedTo: '' });
  }, []);

  // Cancel / ✕ / click-away: CLEAR the form so re-opening starts fresh (no stale
  // selections) (user request 2026-07-14).
  const cancel = useCallback(() => { reset(); onClose(); }, [reset, onClose]);

  const handleOk = useCallback(async () => {
    if (!templateId) { notify.warning('Please choose an EDD Template.'); return; }
    setGenerating(true);
    try {
      // OK = PREVIEW only — nothing is persisted until the Spreadsheet View's Save.
      const res = await api.post('/api/v1/edd/preview', { templateId, filter: filterBody });
      const data = res.data?.data as { eddReportId: string; eddReportOid: string; sheets: EddResult['sheets'] };
      const templateName = templates.find((t) => t.id === templateId)?.name ?? 'EDD';
      setResult({
        eddReportId:  data.eddReportId,   // '' until saved
        eddReportOid: data.eddReportOid,
        templateName,
        sheets: data.sheets ?? [],
        templateId,
        filter: filterBody,
        saved: false,
      });
      onClose();
      navigate('/m/eddresult');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Failed to generate the EDD.';
      notify.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [templateId, filterBody, templates, setResult, onClose, navigate, notify]);

  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(open, { onClose: cancel });

  if (!open) return null;

  const setKey = (key: MultiKey) => (v: string[]) => setMulti((p) => ({ ...p, [key]: v }));

  return createPortal(
    <div className="qs-overlay" onMouseDown={cancel}>
      <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()} {...modalKeys}>
        <div className="qs-titlebar">
          <span className="qs-title"><i className="ti ti-file-database" /> EDD Report Query</span>
          <i className="ti ti-x qs-close" title="Close" onClick={cancel} />
        </div>

        <div className="qs-body">
          {/* qs-grid-wide: this modal's longest caption is "Date Collected From"
              (19 chars), which wraps in the shared 96px label column.  Scoped
              modifier widens ONLY this modal — .qs-field is shared with
              ModuleCrudDialog / ModuleListView / MorReportingPage /
              MorTemplateSetupPage, whose layouts must not shift. */}
          <div className="qs-grid qs-grid-wide">
            {/* Left column — order restored to the previous modal (user request
                2026-07-09): Template · Job ID · Client · Project ID · Project
                Name · Sample Category.  Job ID / Client / Project ID use the rich
                multi-column GRID lookup (EddQsGridCombo, /edd/filter-grid); the
                remaining fills use the plain single-column list (EddQsCombo). */}
            <div className="qs-col">
              <div className="qs-field">
                <label className="qs-label">EDD Template *</label>
                <select className="qs-input" value={templateId}
                  onChange={(e) => { setTemplateId(e.target.value); setMulti(EMPTY_MULTI); }}>
                  <option value="">— Select —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <EddQsField label="Job ID">
                <EddQsGridCombo fillName="JobID" templateId={templateId} filterBody={filterBody}
                  value={multi.JobID} onChange={setKey('JobID')}
                  onRelated={(byCol) => setMulti((prev) => ({
                    ...prev,
                    ClientName:  byCol['Client'] ?? prev.ClientName,
                    ProjectID:   byCol['Project ID'] ?? prev.ProjectID,
                    ProjectName: byCol['Project Name'] ?? prev.ProjectName,
                  }))} />
              </EddQsField>
              <EddQsField label="Client">
                <EddQsGridCombo fillName="ClientName" templateId={templateId} filterBody={filterBody}
                  value={multi.ClientName} onChange={setKey('ClientName')} />
              </EddQsField>
              <EddQsField label="Project ID">
                <EddQsGridCombo fillName="ProjectID" templateId={templateId} filterBody={filterBody}
                  value={multi.ProjectID} onChange={setKey('ProjectID')} />
              </EddQsField>
              <EddQsField label="Project Name">
                <EddQsCombo fillName="ProjectName" templateId={templateId} filterBody={filterBody}
                  value={multi.ProjectName} onChange={setKey('ProjectName')} />
              </EddQsField>
              <EddQsField label="Sample Category">
                <EddQsCombo fillName="SampleCategory" templateId={templateId} filterBody={filterBody}
                  value={multi.SampleCategory} onChange={setKey('SampleCategory')} />
              </EddQsField>
            </div>

            {/* Right column — Test · Method, then the date ranges. */}
            <div className="qs-col">
              <EddQsField label="Test">
                <EddQsCombo fillName="Test" templateId={templateId} filterBody={filterBody}
                  value={multi.Test} onChange={setKey('Test')} />
              </EddQsField>
              <EddQsField label="Method">
                <EddQsCombo fillName="Method" templateId={templateId} filterBody={filterBody}
                  value={multi.Method} onChange={setKey('Method')} />
              </EddQsField>
              <DateField label="Date Received From" dateKey="dateReceivedFrom"
                max={dates.dateReceivedTo} dates={dates} setDates={setDates} />
              <DateField label="Date Received To" dateKey="dateReceivedTo"
                min={dates.dateReceivedFrom} dates={dates} setDates={setDates} />
              <DateField label="Date Collected From" dateKey="dateCollectedFrom"
                max={dates.dateCollectedTo} dates={dates} setDates={setDates} />
              <DateField label="Date Collected To" dateKey="dateCollectedTo"
                min={dates.dateCollectedFrom} dates={dates} setDates={setDates} />
            </div>
          </div>
        </div>

        <div className="qs-footer">
          <div className="qs-footer-actions">
            <button className="ca-btn ca-btn-solid" onClick={handleOk} disabled={!templateId || generating}>
              {generating ? <BusyIcon busy icon="ti-check" /> : <i className="ti ti-check" />} OK
            </button>
            <button className="ca-btn" onClick={reset} disabled={generating}>
              <i className="ti ti-filter-off" /> Clear
            </button>
            <button className="ca-btn" onClick={cancel} disabled={generating}>
              <i className="ti ti-x" /> Cancel
            </button>
          </div>
          <span className="qs-footer-hint">Pick an <b>EDD Template</b>, then click <b>OK</b>.</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** One `.qs-field` label + control row. */
function EddQsField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="qs-field">
      <label className="qs-label">{label}</label>
      {children}
    </div>
  );
}

/** A `Date …` field with a From/To pair of shared QueryDatePickers (Query Panel look). */
/** ONE labelled date bound.  Split out of the old two-pickers-in-a-row DateField
 *  so each bound carries its own caption — "Date Received From" / "Date Received
 *  To" / "Date Collected From" / "Date Collected To" — matching ALPACA's layout
 *  (user request 2026-07-15).  The from ≤ to cross-limit is preserved: the
 *  caller passes the sibling's value as this picker's min/max. */
function DateField({ label, dateKey, min, max, dates, setDates }: {
  label: string;
  dateKey: DateKey;
  min?: string;
  max?: string;
  dates: Record<DateKey, string>;
  setDates: React.Dispatch<React.SetStateAction<Record<DateKey, string>>>;
}) {
  return (
    <div className="qs-field qs-field-date">
      <label className="qs-label">{label}</label>
      <QueryDatePicker ariaLabel={label} value={dates[dateKey]}
        min={min || undefined} max={max || undefined}
        onChange={(v) => setDates((d) => ({ ...d, [dateKey]: v }))} />
    </div>
  );
}

/**
 * `.qs-*`-styled multi-select combo for the EDD cascading fills.  Mirrors the
 * shared QueryCombo look (trigger + portaled checkbox popup) but sources its
 * distinct values from the template-driven `/api/v1/edd/filter-options` endpoint
 * (with the current filter body for cascading).  Disabled until a template is
 * picked.
 */
function EddQsCombo({ fillName, templateId, filterBody, value, onChange }: {
  fillName: MultiKey;
  templateId: string;
  filterBody: unknown;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const disabled = !templateId;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // Skip refetching when the same (template + filter) was already loaded — reopening
  // a dropdown with no change is instant (no server round-trip).
  const loadedSig = useRef<string>('');
  // Cascade on the OTHER fields only — never this field's own selection — so
  // ticking a checkbox here does NOT reload the list (user request 2026-07-09).
  const loadFilter = useMemo(() => {
    const f = { ...(filterBody as Record<string, unknown>) };
    f[FILTER_KEY[fillName]] = [];
    return f;
  }, [filterBody, fillName]);

  const load = useCallback(async (searchTerm: string) => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await api.post('/api/v1/edd/filter-options',
        { templateId, fillName, filter: loadFilter });
      setOptions((res.data?.data ?? []) as string[]);
      loadedSig.current = `${templateId}||${JSON.stringify(loadFilter)}||${searchTerm}`;
    } catch {
      setOptions([]);
      loadedSig.current = '';
    } finally {
      setLoading(false);
    }
  }, [templateId, fillName, loadFilter]);

  const openPanel = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    setInput('');
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Load ONCE when the dropdown opens (and when the cascading filter changes) —
  // the "Type to filter" box below then filters CLIENT-SIDE (instant, no server
  // round-trip / "Loading…" while typing) (user request 2026-07-13).
  // PREFETCH: load the values as soon as a template is picked (and when the
  // cascading filter changes) — in the BACKGROUND, not gated on `open` — so the
  // dropdown is already populated by the time it's opened (no first-open
  // "Loading…") (user request 2026-07-13).  Repeat opens are served from the
  // server-side cache; typing/filtering is client-side (instant).
  useEffect(() => {
    if (!templateId) return;
    const sig = `${templateId}||${JSON.stringify(loadFilter)}||`;
    if (loadedSig.current === sig) return;
    void load('');
  }, [templateId, loadFilter, load]);

  // Client-side filter over the loaded values — instant as you type.
  const opts = useMemo(
    () => (input ? options.filter((o) => o.toLowerCase().includes(input.toLowerCase())) : options),
    [options, input],
  );
  const toggle = (v: string) => {
    const set = new Set(value);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange([...set]);
  };
  const allSelected = opts.length > 0 && opts.every((v) => value.includes(v));
  const selectAll = () => onChange([...new Set([...value, ...opts])]);

  const summary = value.length === 0 ? 'Select…'
    : value.length === 1 ? value[0]
    : `${value.length} selected`;

  return (
    <>
      <button ref={btnRef} type="button" className="qs-combo" onClick={openPanel} disabled={disabled}>
        <span className={value.length ? 'qs-combo-val' : 'qs-combo-ph'}>{summary}</span>
        {value.length
          ? <i className="ti ti-x qs-combo-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }} />
          : <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="qs-combo-backdrop" onMouseDown={() => setOpen(false)} />
          <div className="qs-combo-pop"
            style={{ top: pos.top, left: pos.left, minWidth: Math.max(200, pos.width) }}>
            <div className="qs-combo-search">
              <span className="tb-i">🔍</span>
              <input autoFocus value={input} placeholder="Type to filter…"
                onChange={(e) => setInput(e.target.value)} />
            </div>
            {!loading && opts.length > 0 && (
              <div className="qs-combo-tools">
                <button type="button" className="qs-combo-ftr-btn"
                  onClick={selectAll} disabled={allSelected}>Select all</button>
                <button type="button" className="qs-combo-ftr-btn red"
                  onClick={() => onChange([])} disabled={value.length === 0}>Clear</button>
                <span style={{ flex: 1 }} />
                <button type="button" className="qs-combo-ftr-ok"
                  onClick={() => setOpen(false)}>OK</button>
              </div>
            )}
            <div className="qs-combo-list">
              {loading && <div className="qs-combo-empty">Loading…</div>}
              {!loading && opts.length === 0 && <div className="qs-combo-empty">No values</div>}
              {opts.map((v) => (
                <label key={v} className={`qs-combo-opt${value.includes(v) ? ' sel' : ''}`} title={v}>
                  <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} />
                  <span>{v}</span>
                </label>
              ))}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/**
 * `.qs-*`-styled GRID lookup combo (user request 2026-07-09 — "grid type" with
 * columns, restored from the previous modal but dressed in the SAME Query-Panel
 * chrome as EddQsCombo).  The dropdown is a multi-column GRID (header + per-column
 * filter row + a checkbox per row) sourced from the template-driven
 * `/api/v1/edd/filter-grid`; column 0 is the value that gets selected
 * (Job ID / Client / Project ID).  Same selection semantics as the MUI
 * EddGridCombo — per-ROW signatures so same-name rows check independently, and
 * the DISTINCT column-0 keys are emitted upward (what the EDD filter uses).
 */
function EddQsGridCombo({ fillName, templateId, filterBody, value, onChange, onRelated }: {
  fillName: 'JobID' | 'ClientName' | 'ProjectID';
  templateId: string;
  filterBody: unknown;
  value: string[];
  onChange: (v: string[]) => void;
  /** Called with the DISTINCT values of every non-key column for the currently
   *  selected rows (keyed by column header) — lets the parent auto-fill related
   *  fields (e.g. Job ID selection → Client / Project ID / Project Name). */
  onRelated?: (byColumn: Record<string, string[]>) => void;
}) {
  const disabled = !templateId;
  const [open, setOpen] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<(string | null)[][]>([]);
  const [colFilters, setColFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const loadedSig = useRef<string>('');

  // Cascade on the OTHER fields only — never this field's own selection — so
  // ticking a checkbox here does NOT reload the grid (user request 2026-07-09).
  const loadFilter = useMemo(() => {
    const f = { ...(filterBody as Record<string, unknown>) };
    f[FILTER_KEY[fillName]] = [];
    return f;
  }, [filterBody, fillName]);

  const load = useCallback(async (searchTerm: string) => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await api.post('/api/v1/edd/filter-grid',
        { templateId, fillName, filter: loadFilter });
      const data = res.data?.data as { columns: string[]; rows: (string | null)[][] };
      setColumns(data.columns ?? []);
      setRows(data.rows ?? []);
      // Preserve the user's per-column filters across a server reload; only reset
      // when the column count changes (first load).
      setColFilters((prev) => {
        const n = (data.columns ?? []).length;
        return prev.length === n ? prev : new Array(n).fill('');
      });
      loadedSig.current = `${templateId}||${JSON.stringify(loadFilter)}||${searchTerm}`;
    } catch {
      setColumns([]); setRows([]);
      loadedSig.current = '';
    } finally {
      setLoading(false);
    }
  }, [templateId, fillName, loadFilter]);

  const openPanel = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Load ONCE when the dropdown opens (and when the cascading filter changes) —
  // the column filters below then work purely CLIENT-SIDE (instant, no server
  // round-trip / "Loading…" while typing) (user request 2026-07-13).
  // PREFETCH: load the values as soon as a template is picked (and when the
  // cascading filter changes) — in the BACKGROUND, not gated on `open` — so the
  // dropdown is already populated by the time it's opened (no first-open
  // "Loading…") (user request 2026-07-13).  Repeat opens are served from the
  // server-side cache; typing/filtering is client-side (instant).
  useEffect(() => {
    if (!templateId) return;
    const sig = `${templateId}||${JSON.stringify(loadFilter)}||`;
    if (loadedSig.current === sig) return;
    void load('');
  }, [templateId, loadFilter, load]);

  const filtered = useMemo(() => rows.filter(
    (r) => colFilters.every((f, i) => !f || String(r[i] ?? '').toLowerCase().includes(f.toLowerCase()))),
    [rows, colFilters]);

  // Client-side pagination (100/page) — like the app's other grids (user request
  // 2026-07-09).  Checking a row does NOT change the page.
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows, colFilters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE),
    [filtered, pageSafe]);
  const pageNumbers = useMemo(() => {
    const win = 5;
    const end = Math.min(totalPages, Math.max(pageSafe + 2, win));
    const start = Math.max(1, end - win + 1);
    const arr: number[] = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [pageSafe, totalPages]);

  // Selection is VALUE-DRIVEN by column-0 (the key): a row is checked when its
  // col-0 value is in `value`.  So an EXTERNALLY-set value (e.g. auto-filled from
  // the Job ID selection) shows its rows checked (user request 2026-07-14).
  // Emitting also reports every other column's distinct values so the parent can
  // auto-fill the related fields (Client / Project ID / Project Name).
  const col0 = (r: (string | null)[]) => String(r[0] ?? '').trim();
  // Ticking a row only updates THIS field's selection (no cascade change → the
  // grid does NOT reload while you click).
  const applySelection = (nextValue: string[]) => onChange(nextValue);
  // The related-field auto-fill (Client / Project) is DEFERRED to when the grid
  // CLOSES (OK / click-away) — applying it per-click would change the cascade and
  // reload the grid mid-selection (user request 2026-07-14).
  const emitRelated = () => {
    if (!onRelated) return;
    const sel = new Set(value.map((k) => k.trim()));
    const byCol: Record<string, string[]> = {};
    columns.forEach((c, ci) => {
      if (ci === 0) return;
      const vals = new Set<string>();
      rows.forEach((r) => {
        if (sel.has(col0(r))) { const v = String(r[ci] ?? '').trim(); if (v) vals.add(v); }
      });
      byCol[c] = Array.from(vals);
    });
    onRelated(byCol);
  };
  const closeGrid = () => { emitRelated(); setOpen(false); };
  const toggleRow = (r: (string | null)[]) => {
    const k = col0(r); if (!k) return;
    applySelection(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  };
  const pageKeys = pageRows.map(col0).filter(Boolean);
  const allChecked = pageKeys.length > 0 && pageKeys.every((k) => value.includes(k));
  const toggleAll = () => {
    applySelection(allChecked
      ? value.filter((x) => !pageKeys.includes(x))
      : Array.from(new Set([...value, ...pageKeys])));
  };

  const summary = value.length === 0 ? 'Select…'
    : value.length === 1 ? value[0]
    : `${value.length} selected`;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const popWidth = Math.min(
    Math.max(pos.width, columns.length > 0 ? columns.length * 150 + 60 : 360),
    vw * 0.9);
  const popLeft = Math.max(8, Math.min(pos.left, vw - popWidth - 8));

  return (
    <>
      <button ref={btnRef} type="button" className="qs-combo" onClick={openPanel} disabled={disabled}>
        <span className={value.length ? 'qs-combo-val' : 'qs-combo-ph'}>{summary}</span>
        {value.length
          ? <i className="ti ti-x qs-combo-clear" onClick={(e) => { e.stopPropagation(); applySelection([]); }} />
          : <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="qs-combo-backdrop" onMouseDown={closeGrid} />
          <div className="qs-combo-pop" style={{ top: pos.top, left: popLeft, width: popWidth, minWidth: popWidth }}>
            <div style={{ maxHeight: 340, overflow: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ ...gcTh, width: 30, textAlign: 'center' }}>
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                    </th>
                    {columns.map((c) => <th key={c} style={gcTh}>{c}</th>)}
                  </tr>
                  <tr>
                    <th style={gcTh} />
                    {columns.map((c, i) => (
                      <th key={c} style={{ ...gcTh, fontWeight: 400 }}>
                        <input value={colFilters[i] ?? ''}
                          onChange={(e) => setColFilters((f) => { const n = [...f]; n[i] = e.target.value; return n; })}
                          style={{ width: '100%', boxSizing: 'border-box', font: 'inherit', padding: '2px 4px' }} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={columns.length + 1} className="qs-combo-empty">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={columns.length + 1} className="qs-combo-empty">No matches</td></tr>
                  ) : pageRows.map((r, ri) => {
                    const checked = value.includes(col0(r));
                    return (
                      <tr key={ri} onClick={() => toggleRow(r)}
                        style={{ cursor: 'pointer', background: checked ? 'var(--ai-orange-tint)' : undefined }}>
                        <td style={{ ...gcTd, textAlign: 'center' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleRow(r)}
                            onClick={(e) => e.stopPropagation()} />
                        </td>
                        {columns.map((_, ci) => <td key={ci} style={gcTd}>{r[ci] ?? ''}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="qs-combo-tools" style={{ gap: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--ai-muted, #6b7280)' }}>
                Page {pageSafe} of {totalPages} ({filtered.length})
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" style={gcPageBtn} disabled={pageSafe <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
              {pageNumbers.map((n) => (
                <button key={n} type="button" style={n === pageSafe ? gcPageBtnActive : gcPageBtn}
                  onClick={() => setPage(n)}>{n}</button>
              ))}
              <button type="button" style={gcPageBtn} disabled={pageSafe >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
              <button type="button" className="qs-combo-ftr-ok" style={{ marginLeft: 6 }}
                onClick={closeGrid}>OK</button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

// Column separators use the SAME global token as every other grid
// (--ai-line-soft, the app-wide "grid column separators" colour) — never a
// bespoke/red line (user request 2026-07-09).
const gcTh: CSSProperties = {
  textAlign: 'left', padding: '4px 8px',
  borderBottom: '1px solid var(--ai-line, #e3e6ec)',
  borderRight: '1px solid var(--ai-line-soft, #eef0f3)',
  whiteSpace: 'nowrap', fontWeight: 600, position: 'sticky', top: 0, background: 'var(--ai-header-bg, #fafbfc)',
};
const gcTd: CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid var(--ai-line-soft, #eef0f3)',
  borderRight: '1px solid var(--ai-line-soft, #eef0f3)',
  verticalAlign: 'top',
};
const gcPageBtn: CSSProperties = {
  minWidth: 24, height: 24, padding: '0 6px', fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--ai-line, #d9dde3)', borderRadius: 4,
  background: 'var(--ai-card, #fff)', color: 'var(--ai-ink, #1f2430)',
};
const gcPageBtnActive: CSSProperties = {
  ...gcPageBtn, background: 'var(--ai-orange, #e8670a)', color: '#fff',
  borderColor: 'var(--ai-orange, #e8670a)', fontWeight: 600,
};
